import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Stack,
  CircularProgress, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip,
} from '@mui/material';
import {
  AccountBalance as PayrollIcon,
  People as PeopleIcon,
  TrendingUp as EarningsIcon,
  TrendingDown as DeductionsIcon,
  AccountBalanceWallet as NetIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { payrollApi } from '../../../api/payrollApi';
import { PayCycle, PayCycleLabels, PayrollDashboardDto, PayrollRunStatusLabels, PayrollRunStatusColors } from '../../../types/payrollTypes';

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN');
};

const getRunPeriodLabel = (run: PayrollDashboardDto['latestRun']) => {
  if (!run) return '-';
  if ((run.payCycle ?? PayCycle.Monthly) === PayCycle.Weekly) {
    return `${formatDate(run.periodStart)} - ${formatDate(run.periodEnd)}`;
  }

  return `${run.month}/${run.year}`;
};

const PayrollDashboardPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<PayrollDashboardDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const data = await payrollApi.getDashboard();
        setDashboard(data);
      } catch {
        toast.error('Failed to load payroll dashboard');
      } finally {
        setLoading(false);
      }
    };
    void loadDashboard();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboard) return null;

  const summaryCards = [
    { label: 'Total Employees', value: dashboard.totalEmployees, icon: <PeopleIcon />, color: '#667eea' },
    { label: 'Gross Salary', value: `₹${(dashboard.totalGrossSalary ?? 0).toLocaleString('en-IN')}`, icon: <EarningsIcon />, color: '#66bb6a' },
    { label: 'Total Deductions', value: `₹${(dashboard.totalDeductions ?? 0).toLocaleString('en-IN')}`, icon: <DeductionsIcon />, color: '#ef5350' },
    { label: 'Net Salary', value: `₹${(dashboard.totalNetSalary ?? 0).toLocaleString('en-IN')}`, icon: <NetIcon />, color: '#29b6f6' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>Payroll Dashboard</Typography>

      <Grid container spacing={2} mb={4}>
        {summaryCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
            <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                </Stack>
                <Typography variant="h4" fontWeight={700}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {dashboard.latestRun && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Latest Payroll Run</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Run Period</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Employees</TableCell>
                  <TableCell align="right">Gross Salary</TableCell>
                  <TableCell align="right">Net Salary</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{PayCycleLabels[dashboard.latestRun.payCycle ?? PayCycle.Monthly]}</TableCell>
                  <TableCell>{getRunPeriodLabel(dashboard.latestRun)}</TableCell>
                  <TableCell>
                    <Chip
                      label={PayrollRunStatusLabels[dashboard.latestRun.status]}
                      color={PayrollRunStatusColors[dashboard.latestRun.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{dashboard.latestRun.employeeCount}</TableCell>
                  <TableCell align="right">₹{(dashboard.latestRun.totalGross ?? 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right">₹{(dashboard.latestRun.totalNet ?? 0).toLocaleString('en-IN')}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default PayrollDashboardPage;
